import type { ReactNode } from 'react';

import { Button, EmptyState, ErrorState, IconButton, Spinner } from '@/components/ui';

/**
 * Gemeinsame Gestalt der Unterlisten der Verwaltung (REQ-28 §1): schlichte Tabelle mit einer
 * Kopfzeile — Abschnittstitel links, „Add" rechts — und je Zeile ein Entfernen-Knopf. Die
 * bisherige Zeilenauswahl mit „Delete" in der Werkzeugleiste entfällt dafür.
 */
export interface AdminSubListProps<T> {
  /** Abschnittstitel links in der Kopfzeile, z. B. „Permissions" oder „Views". */
  title: string;
  /** Spaltenköpfe; die Spalte des Entfernen-Knopfes kommt von selbst dazu. */
  columns: readonly string[];
  rows: readonly T[];
  rowKey: (row: T) => number | string;
  /** Die `<td>` einer Zeile ohne die Spalte des Entfernen-Knopfes. */
  renderCells: (row: T) => ReactNode;
  /** Öffnet den Auswahldialog. */
  onAdd: () => void;
  onRemove: (row: T) => void;
  loading: boolean;
  /** `null` oder `undefined`, solange kein Fehler vorliegt. */
  error: unknown;
  onRetry: () => void;
  emptyText: string;
  /** Meldung des Fehlerzustands. */
  errorMessage: string;
  /** Satz unter der Tabelle, z. B. die Regel „eine View, eine Kategorie". */
  hint?: ReactNode;
  /** Sperrt „Add" und die Entfernen-Knöpfe, solange eine Änderung läuft. */
  busy?: boolean;
}

/** Technische Einzelheit für `ErrorState` — die Meldung des Servers, sofern es eine gibt. */
function describeError(error: unknown): string | undefined {
  if (error instanceof Error) return error.message;
  return typeof error === 'string' ? error : undefined;
}

export function AdminSubList<T>(props: AdminSubListProps<T>) {
  const { onRemove, renderCells, rowKey, rows } = props;
  const failed = props.error !== null && props.error !== undefined;

  const body = (): ReactNode => {
    if (props.loading) {
      return (
        <div className="iwm-state-loading">
          <span className="iwm-state-spinner">
            <Spinner size={20} />
          </span>
          Wird geladen …
        </div>
      );
    }
    if (failed) {
      return (
        <ErrorState
          size="sm"
          message={props.errorMessage}
          detail={describeError(props.error)}
          onRetry={props.onRetry}
        />
      );
    }
    if (rows.length === 0) return <EmptyState size="sm" title={props.emptyText} />;

    return (
      <div className="iwm-admin-sublist-wrap">
        <table className="iwm-admin-sublist-table">
          <thead>
            <tr>
              {props.columns.map((column) => (
                <th key={column} scope="col">
                  {column}
                </th>
              ))}
              <th scope="col" className="iwm-admin-sublist-actions">
                <span className="iwm-admin-sr-only">Remove</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={rowKey(row)}>
                {renderCells(row)}
                <td className="iwm-admin-sublist-actions">
                  <IconButton
                    iconClass="fa-solid fa-xmark"
                    title="Remove"
                    disabled={props.busy === true}
                    onClick={() => {
                      onRemove(row);
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <section className="iwm-admin-sublist">
      <div className="iwm-admin-sublist-head">
        <h4 className="iwm-admin-sublist-title">{props.title}</h4>
        <Button
          content="Add"
          iconClass="fa-solid fa-plus"
          disabled={props.busy === true}
          onClick={props.onAdd}
        />
      </div>
      {body()}
      {props.hint === undefined ? null : <p className="iwm-admin-sublist-hint">{props.hint}</p>}
    </section>
  );
}

export default AdminSubList;
